import { useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Illustration } from '@mint-lib/illustrations';
import type {} from '@mint-lib/theme/src/extensions/palette.js';
import { typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { useIntegrationsFilter } from '../../../hooks/useIntegrationsFilter.js';
import useOmniConnectorFlags from '../../../hooks/useOmniConnectorFlags.js';
import { useVirtualPlatformAdding } from '../../../hooks/useVirtualPlatformAdding.js';
import { Platform } from '../../../types/index.js';
import SkeletonsBox from '../../_atoms/SkeletonsBox/SkeletonsBox.jsx';
import PlatformListContainer from '../../_organisms/PlatformListContainer/PlatformListContainer.jsx';
import IntegrationsHeader from '../../features/IntegrationsHeader/IntegrationsHeader.jsx';
import { getFilteredIntegrations } from './IntegrationsList.utils.jsx';

type IntegrationsListType = {
  allIntegrations: Platform[];
};

const IntegrationsList = ({ allIntegrations }: IntegrationsListType) => {
  const { t } = useTranslation('@myn/integrations_v2');
  const omniConnectorFlags = useOmniConnectorFlags();

  const isIntegrationsLoading = !allIntegrations.length;

  const {
    search: searchValue,
    status: statusFilter,
    sort: sortField,
    categoryArray,
    channelArray,
  } = useIntegrationsFilter();

  const filteredIntegrations = useMemo(() => {
    return getFilteredIntegrations(
      {
        category: categoryArray,
        channel: channelArray,
        searchValue,
        statusFilter,
        sortField,
      },
      allIntegrations,
      omniConnectorFlags,
    );
  }, [
    allIntegrations,
    statusFilter,
    sortField,
    searchValue,
    categoryArray,
    channelArray,
    omniConnectorFlags,
  ]);

  const { finalFilteredIntegrations, onIntegrateButtonClick } =
    useVirtualPlatformAdding(filteredIntegrations);

  return (
    <div className="mr-2 ml-4 h-full w-full pb-0">
      <IntegrationsHeader isIntegrationsLoading={isIntegrationsLoading} />

      {isIntegrationsLoading ? (
        <SkeletonsBox
          skeletonCount={16}
          skeletonClassName="rounded h-52"
          wrapperClassName="grid grid-cols-4 gap-4 mt-4"
        />
      ) : null}

      {/* integrations no-item-found */}
      {!isIntegrationsLoading &&
      allIntegrations.length !== 0 &&
      finalFilteredIntegrations.length === 0 ? (
        <div className="flex h-[calc(100vh-200px)] w-full flex-col items-center justify-center">
          <Illustration className="size-40" component="IllustrationNoResults" />
          <h6
            className={cn(typographyVariants({ variant: 'h6' }), 'mt-1 mb-1')}
          >
            {t('No results found')}
          </h6>
          <p className={typographyVariants({ variant: 'bodyShort01' })}>
            {t(
              'Try adjusting your search or filters in order to find what you are looking for.',
            )}
          </p>
        </div>
      ) : null}

      {/* integrations filtered list */}
      {!isIntegrationsLoading && finalFilteredIntegrations.length !== 0 ? (
        <PlatformListContainer
          filteredIntegrations={finalFilteredIntegrations}
          onIntegrateButtonClick={onIntegrateButtonClick}
        />
      ) : null}
    </div>
  );
};

export default IntegrationsList;
